import { useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  type DataTableToolbarProps,
  DataTableViewOptions,
  DateUI,
  type FacetedFilterOption,
  FacetedFilterStandalone,
  type GetOptionsByValueBase,
  Number,
  type OptionsByValue,
  PrimaryFullScreenSpinner,
  StatusUI,
} from "@repo/ui";
import { opsHomeCurrency, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { compareAsc } from "date-fns";
import { X } from "lucide-react";
import { Suspense, useEffect, useMemo } from "react";
import { PiPlus } from "react-icons/pi";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import { assetClassValues, assetStatusValues } from "~/gql/client/enumValues";
import type { AdminAssetListAssetFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

type ExternalAccountDetailsTypename = Exclude<
  Extract<
    AdminAssetListAssetFragment,
    { __typename: "ExternalBankAccountAsset" }
  >["details"],
  null | undefined
>["__typename"];

const externalDataSourceLabelByTypename: Record<
  ExternalAccountDetailsTypename,
  string
> = {
  AirwallexExternalAccountDetails: "Airwallex",
  AkahuExternalAccountDetails: "Akahu",
  BrexExternalAccountDetails: "Brex",
  JpmcExternalAccountDetails: "JPMorgan Chase",
  ManualEntryExternalAccountDetails: "Manual Entry",
  MercuryExternalAccountDetails: "Mercury",
  PlaidExternalAccountDetails: "Plaid",
  QuilttExternalAccountDetails: "Quiltt",
  RampExternalAccountDetails: "Ramp",
  SaltEdgeExternalAccountDetails: "Salt Edge",
  SissExternalAccountDetails: "SISS",
  SkriptExternalAccountDetails: "Skript",
  StripeExternalAccountDetails: "Stripe",
};

export const Route = createFileRoute("/__auth/assets")({
  head: () => opsHead("Assets"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminAssetList($opsHomeCurrency: Currency) {
        appEnvironment {
          id
          environment
        }
        organisations {
          id
          name
          assets(includeInactive: true) {
            id
            ...AdminAssetListAsset
          }
        }
      }

      fragment AdminAssetListAsset on Asset {
        id
        openedAt
        status
        class
        feeRate
        businessEntity {
          id
          name
          organisation {
            id
            name
          }
        }
        ...AssetCardFields
        estimatedValue {
          amount
          currency
        }
        estimatedValueInOpsHomeCurrency: estimatedValue(currency: $opsHomeCurrency) {
          amount
          currency
        }
      }
    `),
    {
      variables: {
        opsHomeCurrency,
      },
    },
  );

  const assets =
    data.organisations?.flatMap((o) => o.assets).filter((a) => !!a) ?? [];

  return (
    <AdminPageContainer testId="admin-assets">
      <AdminPageHeader
        title="Assets"
        subtitle="Store of client funds"
        buttons={
          <div className="flex gap-2">
            {data.appEnvironment?.environment !== "PRODUCTION" && (
              <Link to="/assets/demo-new">
                <Button variant="secondary" leftIcon={<PiPlus />}>
                  New demo asset
                </Button>
              </Link>
            )}
            <Link to="/assets/new">
              <Button variant="primary" leftIcon={<PiPlus />}>
                New asset
              </Button>
            </Link>
          </div>
        }
      />
      <div>
        {assets.length === 0 ? (
          <div>No assets</div>
        ) : (
          <div>
            <DataTable
              data={assets}
              columns={columns}
              onRowClick={(row) =>
                void navigate({
                  to: "/assets/$assetId",
                  params: { assetId: row.original.id },
                })
              }
              columnSortProps={{
                id: "estimatedValue",
                desc: true,
              }}
              pageSize={50}
              className="overflow-hidden border"
              toolbarComponent={DataTableToolbar}
              initialState={{
                columnFilters: [{ id: "status", value: ["ACTIVE"] }],
              }}
              initialColumnVisibility={{
                openedAt: false,
                lastSyncedAt: false,
                // filter only columns
                organisationId: false,
                providerName: false,
                externalDataSource: false,
                class: false,
              }}
            />
          </div>
        )}
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<AdminAssetListAssetFragment>[] = [
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      return <StatusUI status={row.original.status} />;
    },
    filterFn: (row, _id, value) => value.includes(row.original.status),
  },
  {
    id: "openedAt",
    accessorFn: (row) => row.openedAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Opened" />
    ),
    cell: ({ row }) => {
      const date = row.original.openedAt;
      return date ? <DateUI date={date} delta /> : null;
    },
    sortingFn: (a, b) =>
      compareAsc(
        a.original.openedAt ?? new Date(),
        b.original.openedAt ?? new Date(),
      ),
  },
  {
    id: "businessEntityId",
    accessorFn: (row) => row.businessEntity?.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Business Entity" />
    ),
    cell: ({ row }) => (
      <BusinessEntityColumnCell businessEntity={row.original.businessEntity} />
    ),
    filterFn: (row, _id, value) =>
      value.includes(row.original.businessEntity?.id),
  },
  {
    id: "id",
    accessorFn: (row) => row.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    cell: ({ row }) => {
      return (
        <AccountCard
          variant="DEFAULT"
          account={row.original}
          subText="PROVIDER"
        />
      );
    },
    filterFn: (row, _id, value) => value.includes(row.original.id),
  },
  {
    id: "estimatedValue",
    accessorFn: (row) => row.estimatedValue?.amount,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Current Balance"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          <CurrencyAmountUI
            amount={row.original.estimatedValue?.amount}
            currency={row.original.estimatedValue?.currency}
          />

          {row.original.estimatedValueInOpsHomeCurrency?.currency !==
            row.original.estimatedValue?.currency && (
            <CurrencyAmountUI
              amount={row.original.estimatedValueInOpsHomeCurrency?.amount}
              currency={row.original.estimatedValueInOpsHomeCurrency?.currency}
            />
          )}
        </div>
      );
    },
    sortingFn: (a, b) =>
      compareAsc(
        a.original.estimatedValueInOpsHomeCurrency?.amount.toNumber() ?? 0,
        b.original.estimatedValueInOpsHomeCurrency?.amount.toNumber() ?? 0,
      ),
  },
  {
    id: "feeRate",
    accessorFn: (row) => row.feeRate?.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fee Rate" align="right" />
    ),
    cell: ({ row }) => {
      const { feeRate } = row.original;
      return (
        <div className="text-right">
          {feeRate ? (
            <Number
              value={feeRate}
              format={Number.format.percentage({
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            />
          ) : (
            "-"
          )}
        </div>
      );
    },
    sortingFn: (a, b) =>
      (a.original.feeRate?.toNumber() ?? 0) -
      (b.original.feeRate?.toNumber() ?? 0),
  },
  {
    id: "lastSyncedAt",
    accessorFn: (row) => getAssetLastSyncedAt(row),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Last synced at" />
    ),
    cell: ({ row }) => {
      const lastSyncedAt = getAssetLastSyncedAt(row.original);
      return lastSyncedAt ? (
        <DateUI date={lastSyncedAt} variant="DATETIME_TZ" />
      ) : null;
    },
    sortingFn: (a, b) =>
      compareAsc(
        getAssetLastSyncedAt(a.original) ?? new Date(0),
        getAssetLastSyncedAt(b.original) ?? new Date(0),
      ),
  },
  // Filter only columns
  {
    id: "organisationId",
    accessorFn: (row) => row.businessEntity?.organisation?.id,
    filterFn: (row, _id, value) =>
      value.includes(row.original.businessEntity?.organisation?.id),
  },
  {
    id: "providerName",
    accessorFn: (row) => row.providerName,
    filterFn: (row, _id, value) => value.includes(row.original.providerName),
  },
  {
    id: "externalDataSource",
    accessorFn: (row) => getAssetExternalDataSourceTypename(row),
    filterFn: (row, _id, value) => {
      const externalDataSource = getAssetExternalDataSourceTypename(
        row.original,
      );

      return externalDataSource ? value.includes(externalDataSource) : false;
    },
  },
  {
    id: "class",
    accessorFn: (row) => row.class,
    filterFn: (row, _id, value) => value.includes(row.original.class),
  },
];

function getAssetLastSyncedAt(asset: AdminAssetListAssetFragment) {
  return "details" in asset ? asset.details?.lastSyncedAt : null;
}

function getAssetExternalDataSourceTypename(
  asset: AdminAssetListAssetFragment,
): ExternalAccountDetailsTypename | null {
  if (asset.__typename !== "ExternalBankAccountAsset") {
    return null;
  }

  return asset.details?.__typename ?? null;
}

function DataTableToolbar({
  table,
}: DataTableToolbarProps<AdminAssetListAssetFragment>) {
  const classFilterValue = table.getColumn("class")?.getFilterValue();
  const statusFilterValue = table.getColumn("status")?.getFilterValue();

  const shouldShowLastSyncedAt =
    (Array.isArray(classFilterValue) &&
      classFilterValue.includes("EXTERNAL_BANK_ACCOUNT")) ||
    (Array.isArray(statusFilterValue) &&
      statusFilterValue.includes("DISABLED"));

  useEffect(() => {
    const lastSyncedAtColumn = table.getColumn("lastSyncedAt");

    if (!lastSyncedAtColumn) {
      return;
    }

    lastSyncedAtColumn.toggleVisibility(shouldShowLastSyncedAt);
  }, [shouldShowLastSyncedAt, table]);

  const isFiltered =
    table.getPreFilteredRowModel().rows.length >
    table.getFilteredRowModel().rows.length;

  const preFilteredRows = table.getPreFilteredRowModel().rows;

  const organisationIdOptionsByValue = useMemo<OptionsByValue>(
    () => getOrganisationIdOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  const businessEntityIdOptionsByValue = useMemo<OptionsByValue>(
    () => getBusinessEntityIdOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  const providerNameOptionsByValue = useMemo<OptionsByValue>(
    () => getProviderOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  const externalDataSourceOptionsByValue = useMemo<OptionsByValue>(
    () => getExternalDataSourceOptionsByValue({ preFilteredRows }),
    [preFilteredRows],
  );

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-1">
        {table.getColumn("status") && (
          <FacetedFilterStandalone
            column={table.getColumn("status")}
            title="Status"
            options={assetStatusValues.map((value) => ({
              label: prettyPrintEnum(value),
              value,
            }))}
          />
        )}
        {table.getColumn("organisationId") && (
          <FacetedFilterStandalone
            column={table.getColumn("organisationId")}
            title="Organisation"
            options={Object.values(organisationIdOptionsByValue)}
          />
        )}
        {table.getColumn("businessEntityId") && (
          <FacetedFilterStandalone
            column={table.getColumn("businessEntityId")}
            title="Business Entity"
            options={Object.values(businessEntityIdOptionsByValue)}
          />
        )}
        {table.getColumn("providerName") && (
          <FacetedFilterStandalone
            column={table.getColumn("providerName")}
            title="Provider"
            options={Object.values(providerNameOptionsByValue)}
          />
        )}
        {table.getColumn("externalDataSource") && (
          <FacetedFilterStandalone
            column={table.getColumn("externalDataSource")}
            title="External Data Source"
            options={Object.values(externalDataSourceOptionsByValue)}
          />
        )}
        {table.getColumn("class") && (
          <FacetedFilterStandalone
            column={table.getColumn("class")}
            title="Kind"
            options={assetClassValues.map((value) => ({
              label: prettyPrintEnum(value),
              value,
            }))}
          />
        )}
        {isFiltered && (
          <Button
            rightIcon={<X />}
            variant="ghost"
            size="sm"
            onClick={() => table.resetColumnFilters()}
          >
            Reset
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}

type GetOptionsByValue = GetOptionsByValueBase<AdminAssetListAssetFragment>;

const getBusinessEntityIdOptionsByValue: GetOptionsByValue = ({
  preFilteredRows,
}) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const businessEntity = row.original.businessEntity;

      if (businessEntity && !acc[businessEntity.id]) {
        acc[businessEntity.id] = {
          label: businessEntity.name,
          value: businessEntity.id,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );

const getExternalDataSourceOptionsByValue: GetOptionsByValue = ({
  preFilteredRows,
}) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const externalDataSource = getAssetExternalDataSourceTypename(
        row.original,
      );

      if (externalDataSource && !acc[externalDataSource]) {
        acc[externalDataSource] = {
          label: externalDataSourceLabelByTypename[externalDataSource],
          value: externalDataSource,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );

const getOrganisationIdOptionsByValue: GetOptionsByValue = ({
  preFilteredRows,
}) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const organisation = row.original.businessEntity?.organisation;

      if (organisation && !acc[organisation.id]) {
        acc[organisation.id] = {
          label: organisation.name,
          value: organisation.id,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );

const getProviderOptionsByValue: GetOptionsByValue = ({ preFilteredRows }) =>
  preFilteredRows.reduce(
    (acc, row) => {
      const provider = row.original.providerName;

      if (provider && !acc[provider]) {
        acc[provider] = {
          label: provider,
          value: provider,
        };
      }
      return acc;
    },
    {} as Record<string, FacetedFilterOption>,
  );
